import { Blocks, BookOpen, Boxes, Brain, Building2, History, House, KeyRound, LayoutTemplate, MessagesSquare, Network, Plug, ShieldCheck, Sparkles, UserCog } from 'lucide-react';
import type { ElementType } from 'react';

import type { AttentionArea } from '@/api/adminClient';
import { HANDBOOK_URL } from '@/handbook/handbookUrls';
import { type AdminAccess, canOpenPath } from '@/lib/platformAdmin';

export interface NavItem {
  path: string;
  label: string;
  icon: ElementType;
  group: 'back' | 'org' | 'directory' | 'build' | 'platform' | 'reference';
  /** Another site (an absolute URL in `path`): shown to everyone, never active here. */
  external?: boolean;
  /** The Home "Needs attention" area whose count shows beside it. */
  badge?: AttentionArea;
}

export const NAV_ITEMS: NavItem[] = [
  { path: '/', label: 'Home', icon: House, group: 'directory' },
  { path: '/organizations', label: 'Organizations', icon: Building2, group: 'directory', badge: 'organizations' },
  { path: '/users', label: 'Users', icon: UserCog, group: 'directory' },
  { path: '/permissions', label: 'Permissions', icon: ShieldCheck, group: 'directory' },
  { path: '/integrations', label: 'Integrations', icon: Plug, group: 'build' },
  { path: '/integrations/builder', label: 'Integration Builder', icon: Blocks, group: 'build', badge: 'integrations' },
  { path: '/templates', label: 'Templates', icon: LayoutTemplate, group: 'build', badge: 'templates' },
  { path: '/skills', label: 'Skills', icon: Sparkles, group: 'build' },
  { path: '/platform', label: 'Platform', icon: Boxes, group: 'platform', badge: 'platform' },
  { path: '/runs', label: 'Runs', icon: History, group: 'platform' },
  { path: '/cowork', label: 'Cowork', icon: MessagesSquare, group: 'platform' },
  { path: '/ai', label: 'AI', icon: Brain, group: 'platform' },
  { path: '/fabrics', label: 'Fabrics', icon: Network, group: 'platform' },
  { path: '/token-health', label: 'Token health', icon: KeyRound, group: 'platform', badge: 'tokenHealth' },
];

/** Headings shown above each group while the sidebar is expanded; a collapsed rail keeps the dividers. */
export const NAV_GROUP_LABELS: Partial<Record<NavItem['group'], string>> = {
  directory: 'Customers',
  build: 'Build',
  platform: 'Operations',
};

/** Pinned to the foot of the sidebar, out of the working path. The Handbook site checks its own role. */
export const PINNED_NAV_ITEMS: NavItem[] = [
  { path: HANDBOOK_URL, label: 'Handbook', icon: BookOpen, group: 'reference', external: true },
];

/** The heading above the item at `index` when it opens a labelled group. */
export function groupHeading(items: NavItem[], index: number): string | undefined {
  const item = items[index];
  if (!item || items[index - 1]?.group === item.group) return undefined;
  return NAV_GROUP_LABELS[item.group];
}

/** The count beside a nav item: its area's "Needs attention" rows, or 0 for no badge. */
export function navBadgeCount(item: NavItem, counts: Partial<Record<AttentionArea, number>> | undefined): number {
  if (!item.badge || !counts) return 0;
  const count = counts[item.badge] ?? 0;
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
}

/** A badge stays two digits wide. */
export function badgeLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

const ALL_NAV_ITEMS = [...NAV_ITEMS, ...PINNED_NAV_ITEMS];

/** Integration builders and template curators without platform admin see only what they can open. */
export function navItemsFor(items: NavItem[], access: AdminAccess): NavItem[] {
  return items.filter((item) => item.external || canOpenPath(item.path, access));
}

function matchesPath(currentPath: string, path: string) {
  if (path === '/') return currentPath === '/';
  return currentPath === path || currentPath.startsWith(`${path}/`);
}

/** The most specific matching nav item wins over a parent path. */
export function isActivePath(currentPath: string, path?: string) {
  if (!path || !matchesPath(currentPath, path)) return false;
  return !ALL_NAV_ITEMS.some((item) => item.path.length > path.length && matchesPath(currentPath, item.path));
}
